/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { Datacenter } from "../DatacenterData";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";

interface InfluxKey {
  id: number;
  keyInflux: string;
  isActive: boolean;
}

const influxKeysColumns: GenericTableColumn<InfluxKey>[] = [
  {
    header: "ID",
    accessor: "id",
    renderCell: (row) => <span className="text-sm text-semantic-text-secondary">{row.id}</span>,
  },
  {
    header: "Key Influx",
    accessor: "keyInflux",
    renderCell: (row) => <span className="text-sm text-semantic-text-secondary">{row.keyInflux || "-"}</span>,
  },
  {
    header: "Status",
    accessor: "isActive",
    renderCell: (row) => {
      const variant = row.isActive ? "neutral" : "default";
      return (
        <GenericTag
          variant={variant}
          label={row.isActive ? "Active" : "Inactive"}
          size="small"
        />
      );
    },
  },
];

const InfluxKeysTable: React.FC<{ data: InfluxKey[] }> = ({ data }) => {
  return (
    <GenericTable
      columns={influxKeysColumns}
      data={data}
      width="100%"
      initialRowsPerPage={10}
      rowsPerPageOptions={[10, 20, 25]}
      tableClassName="bg-semantic-bg-primary"
      maxTableBodyHeight="300px"
    />
  );
};

export interface DatacenterViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  datacenter: Datacenter | null;
  onClose: () => void;
}

export const DatacenterViewModal: React.FC<DatacenterViewModalProps> = ({
  isOpen,
  isLoading,
  datacenter,
  onClose,
}) => {
  return (
    <GenericModal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="View Datacenter Details"
      customWidth="max-w-6xl"
    >
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
          <div>
            <SkeletonBlock h={20} w="40%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        datacenter && (
          <div className="space-y-6">
            {/* Basic Information */}
            <GenericCard 
            isCollapsible={false}
            headerContent={<h3 className="text-md font-semibold text-semantic-text-primary">Basic Information</h3>}
            className="p-5">
              <div className="grid grid-cols-3 gap-4 p-3">
                <GenericInput
                  label="Datacenter ID"
                  value={datacenter.datacenterId || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Name"
                  value={datacenter.name || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Display Name"
                  value={datacenter.displayName || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Address"
                  value={datacenter.direction || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="City"
                  value={datacenter.city || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="GPS"
                  value={datacenter.gps || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Number of Hardware"
                  value={String(datacenter.numberOfHardware ?? 0)}
                  readOnly
                />
                <GenericInput
                  label="Visible"
                  value={datacenter.visible ? "Yes" : "No"}
                  readOnly
                />
                <div className="flex items-center justify-end gap-2">
                  <label className="text-sm font-medium text-semantic-text-secondary">
                    Status:
                  </label>
                  <GenericTag
                    label={datacenter.status || "Unknown"}
                    variant={datacenter.status === "Active" ? "neutral" : "default"}
                    size="small"
                  />
                </div>
              </div>
            </GenericCard>

            {/* Provider Information */}
            {datacenter.provider && (
              <GenericCard 
              isCollapsible={true}
              initialExpanded={true}
              headerContent={<h3 className="text-md font-semibold text-semantic-text-primary">Provider Information</h3>}
              className="p-5">
                <div className="grid grid-cols-3 gap-4 p-3">
                  <GenericInput
                    label="Provider Name"
                    value={datacenter.provider.name || "N/A"}
                    readOnly
                  />
                  <GenericInput
                    label="Provider ID"
                    value={String(datacenter.providerId || "N/A")}
                    readOnly
                  />
                  <GenericInput
                    label="Provider ID Addon"
                    value={datacenter.provider.idAddon || "N/A"}
                    readOnly
                  />
                </div>
              </GenericCard>
            )}

            {/* Capacity and Infrastructure */}
            <GenericCard             
              isCollapsible={true}
              initialExpanded={true}
              headerContent={<h3 className="text-md font-semibold text-semantic-text-primary">Capacity and Infrastructure</h3>}
              className="p-5">
              <div className="grid grid-cols-3 gap-4 p-3">
                <GenericInput
                  label="Capacity (KW)"
                  value={datacenter.capacityKW || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Phase"
                  value={datacenter.phase || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Cage"
                  value={datacenter.cage || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Rack ID"
                  value={datacenter.rackId || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Rack Capacity"
                  value={datacenter.rackCapacity || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Patch Panel ODF"
                  value={datacenter.patchPanelODF || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Rack Security Pin"
                  value={datacenter.rackSecurityPin || "N/A"}
                  readOnly
                />
              </div>
            </GenericCard>


            {/* Influx Keys */}
            {datacenter.influxKeys && datacenter.influxKeys.length > 0 && (
              <GenericCard 
              isCollapsible={false}
              headerContent={<h3 className="text-md font-semibold text-semantic-text-primary">Influx Keys</h3>}
              className="p-5">
                <InfluxKeysTable data={datacenter.influxKeys} />
              </GenericCard>
            )}

          </div>
        )
      )}

      <div className="flex justify-end mt-6">
        <GenericButton label="Close" variant="secondary" onClick={onClose} />
      </div>
    </GenericModal>
  );
};
